import React, { useContext, useEffect, useState, Suspense } from "react";
import {
    Autocomplete,
    Box,
    Button,
    Card,
    Dialog,
    DialogContent,
    DialogTitle,
    Divider,
    Grid,
    IconButton,
    TextField,
    Typography,
    Tabs,
    Tab,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    CircularProgress,
} from "@mui/material";

import { styled, useTheme } from "@mui/material/styles";
import { tokens } from "../../../../theme";
import ReactApexChart from 'react-apexcharts';
import useApi from "../../../shared/hooks/useApi";
import { chartOptions } from "./chartOptions";
import { DemoContainer } from '@mui/x-date-pickers/internals/demo';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import dayjs from "dayjs";
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';

import FilledOperationalModeFinalChart from "./FilledOperationalModeFinalChart";
import FilledOperationalModePreChart from "./FilledOperationalModePreChart";
import FilledOperationalModePreCharColumn from "./FilledOperationalModePreCharColumn";

// Lazy load the JSON editor for better performance
const EnhancedJsonEditor = React.lazy(() => import("./JsonEditor"));

const Custom = ({ isPlanningOpen, setIsPlanningOpen, isSettingsOpen, setIsSettingsOpen }) => {
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);
    const baseUrl = "/esid-24-031";
    const { apiPost, apiGet, loadingApi, error } = useApi();
    const [columns, setColumns] = useState([]);
    const [chartData, setChartData] = useState({
        series: [],
        options: chartOptions.options
    });

    const [openOptionList, setOpenOptionList] = useState(false);
    const [from, setFrom] = useState(dayjs().set("hour", 0));
    const [to, setTo] = useState(dayjs().set("hour", 24));
    const [applyFrom, setApplyFrom] = useState(dayjs().set("hour", 0));
    const [applyTo, setApplyTo] = useState(dayjs().set("hour", 24));
    const [selectedOptions, setSelectedOptions] = useState([
        'BEL_BRU_UTI_CHP_HT_JY310-T',
        'BEL_BRU_UTI_CHP_LT_JY320-T',
        'BEL_BRU_UTI_CHP_NG_JY400-T',
    ]);
    const [loadingGetColumns, setLoadingGetColumns] = useState(false);
    const [loadingGetData, setLoadingGetData] = useState(false);
    const [chpData, setChpData] = useState(null);
    const [afrrProposalData, setAfrrProposalData] = useState(null);
    const [currentTab, setCurrentTab] = useState(0);
    const [rawData, setRawData] = useState(null);

    useEffect(() => {
        //setLoadingGetColumns(true);
        try {
            const getColumnsRoute = `${baseUrl}/get-columns`;
            const getColumns = async () => {
                try {
                    const response = await apiGet("UX_HTTP_API", getColumnsRoute);
                    // Add error checking and provide default values
                    console.log("Response 0311111", response)
                    const realtimeSetpoints = response?.realtime_setpoints || [];
                    const realtimeValues = response?.realtime_values || [];
                    const fullCols = [...realtimeSetpoints, ...realtimeValues];
                    setColumns(fullCols);
                } catch (error) {
                    console.error("Error in getColumns:", error);
                    // Set empty array as fallback
                    setColumns([]);
                } finally {
                    setLoadingGetColumns(false);
                }
            };

            getColumns();
        } catch (error) {
            console.error("Error getting columns:", error);
            setColumns([]);
            setLoadingGetColumns(false);
        }
    }, []);

    useEffect(() => {
        const fromDate = from.toISOString();  // fromDate is your Dayjs object
        const toDate = to.toISOString();      // toDate is your Dayjs object
        const getDataRoute = `${baseUrl}/get-data?from=${fromDate}&to=${toDate}&columns=${encodeURIComponent(selectedOptions.join(","))}`;
        try {
            const getData = async () => {
                //setLoadingGetData(true);
                const data = await apiGet("UX_HTTP_API", getDataRoute);
                if (data) {
                    const series = Object.entries(data).map(([key, dataPoints]) => ({
                        name: key,
                        data: dataPoints.map(point => ({
                            x: new Date(point.datetime).getTime(),
                            y: Math.floor(point.value)
                        }))
                    }));

                    setChartData(prev => ({
                        ...prev,
                        series
                    }));
                    setRawData(data); // Store raw data for JSON tab
                    setLoadingGetData(false);
                }
            };
            const getChpData = async () => {
                let oneBeforeFromDate = dayjs(from).subtract(1, 'day').toISOString();
                const getChpDataRoute = `/esid-24-031/get-chp-final-data?from=${oneBeforeFromDate}&to=${toDate}`;
                try {
                    const data = await apiGet("UX_HTTP_API", getChpDataRoute);
                    // Transform the data structure to flatten date-based keys
                    if (data) {
                        const flattenedData = [];
                        Object.keys(data).forEach(dateKey => {
                            if (data[dateKey] && data[dateKey]['CHP-01']) {
                                flattenedData.push(...data[dateKey]['CHP-01']);
                            }
                        });
                        // Sort by datetime to ensure chronological order
                        flattenedData.sort((a, b) => new Date(a.datetime) - new Date(b.datetime));
                        setChpData({ 'CHP': { 'CHP-01': flattenedData } });
                    } else {
                        setChpData(null);
                    }
                } catch (error) {
                    console.error('Error fetching data:', error);
                    setChpData(null);
                }
            }
            const getChpPreData = async () => {
                let oneBeforeFromDate = dayjs(from).subtract(1, 'day').toISOString();
                const getChpPreDataRoute = `/esid-24-031/get-chp-pre-data?from=${oneBeforeFromDate}&to=${toDate}`;
                try {
                    const data = await apiGet("UX_HTTP_API", getChpPreDataRoute);
                    console.log("data", data)
                    // Extract AFRR-PROPOSAL data from the same response
                    // The data is nested under date keys, so we need to find it
                    if (data) {
                        // Look for AFRR-PROPOSAL data in any of the date keys
                        const afrrData = Object.values(data).find(dateData => 
                            dateData && dateData['AFRR-PROPOSAL']
                        );
                        if (afrrData && afrrData['AFRR-PROPOSAL']) {
                            setAfrrProposalData({ 'AFRR-PROPOSAL': afrrData['AFRR-PROPOSAL'] });
                        }
                    }
                } catch (error) {
                    console.error('Error fetching data:', error);
                }
            }
            getData();
            getChpData();
            getChpPreData();
        } catch (error) {
            console.error("Error getting data:", error);
            setLoadingGetData(false);
        } finally {
            setLoadingGetData(false);
        }
    }, []);
    console.log("gg afrrProposalData", afrrProposalData)
    console.log("gg chpData", chpData)

    const applyFilters = () => {
        try {
            const fromDate = from.toISOString();  // fromDate is your Dayjs object
            const toDate = to.toISOString();      // toDate is your Dayjs object
            const getDataRoute = `${baseUrl}/get-data?from=${fromDate}&to=${toDate}&columns=${encodeURIComponent(selectedOptions.join(","))}`;
            
            // Add a console.log to see the final URL
            setApplyFrom(from);
            setApplyTo(to);
            try {
                const getData = async () => {
                    console.log("inFIlters", getDataRoute)
                    //setLoadingGetData(true);
                    const data = await apiGet("UX_HTTP_API", getDataRoute);
                    if (data) {
                        const series = Object.entries(data).map(([key, dataPoints]) => ({
                            name: key,
                            data: dataPoints.map(point => ({
                                x: new Date(point.datetime).getTime(),
                                y: Math.floor(point.value)
                            }))
                        }));

                        setChartData(prev => ({
                            ...prev,
                            series
                        }));
                        setRawData(data); // Store raw data for JSON tab
                        setLoadingGetData(false);
                    }
                };
                const getChpData = async () => {
                    const getChpDataRoute = `/esid-24-031/get-chp-final-data?from=${fromDate}&to=${toDate}`;
                    try {
                        const data = await apiGet("UX_HTTP_API", getChpDataRoute);
                        // Transform the data structure to flatten date-based keys
                        if (data) {
                            const flattenedData = [];
                            Object.keys(data).forEach(dateKey => {
                                if (data[dateKey] && data[dateKey]['CHP-01']) {
                                    flattenedData.push(...data[dateKey]['CHP-01']);
                                }
                            });
                            // Sort by datetime to ensure chronological order
                            flattenedData.sort((a, b) => new Date(a.datetime) - new Date(b.datetime));
                            setChpData({ 'CHP': { 'CHP-01': flattenedData } });
                        } else {
                            setChpData(null);
                        }
                    } catch (error) {
                        console.error('Error fetching data:', error);
                        setChpData(null);
                    }
                }
                const getChpPreData = async () => {
                    const getChpPreDataRoute = `/esid-24-031/get-chp-pre-data?from=${fromDate}&to=${toDate}`;
                    try {
                        const data = await apiGet("UX_HTTP_API", getChpPreDataRoute);
                        // Extract AFRR-PROPOSAL data from the same response
                        // The data is nested under date keys, so we need to find it
                        if (data) {
                            // Look for AFRR-PROPOSAL data in any of the date keys
                            const afrrData = Object.values(data).find(dateData => 
                                dateData && dateData['AFRR-PROPOSAL']
                            );
                            if (afrrData && afrrData['AFRR-PROPOSAL']) {
                                setAfrrProposalData({ 'AFRR-PROPOSAL': afrrData['AFRR-PROPOSAL'] });
                            }
                        }
                    } catch (error) {
                        console.error('Error fetching data:', error);
                    }
                }
                getData();
                getChpData();
                getChpPreData();
            } catch (error) {
                console.error("Error applying filters:", error);
                setLoadingGetData(false);
            } finally {
                setLoadingGetData(false);
            }
        } catch (error) {
            console.error("Error applying filters:", error);
            setLoadingGetData(false);
        }
    }

    const removeSelectedOption = (option) => {
        setSelectedOptions(selectedOptions.filter(item => item !== option));
    }

    const handleTabChange = (event, newValue) => {
        setCurrentTab(newValue);
    };

    const renderGraphsTab = () => (
        <>
            <Box sx={{ display: "flex", flexWrap: "wrap", justifyContent: "flex-start", mt: 2, mb: 4 }}>
                <Box >
                    <LocalizationProvider dateAdapter={AdapterDayjs} sx={{ margin: 2 }}>
                        <DemoContainer components={['DatePicker']}>
                            <DatePicker format="DD/MM/YYYY" label="From" value={from} onChange={(date) => setFrom(date)} />
                        </DemoContainer>
                    </LocalizationProvider>
                </Box>
                <Box sx={{ marginLeft: 10 }}>
                    <LocalizationProvider dateAdapter={AdapterDayjs} sx={{ margin: 2 }}>
                        <DemoContainer components={['DatePicker']}>
                            <DatePicker format="DD/MM/YYYY"  label="To" value={to} onChange={(date) => setTo(date)} />
                        </DemoContainer>
                    </LocalizationProvider>
                </Box>
            </Box>
            <Box sx={{ display: "flex", flexWrap: "wrap", justifyContent: "flex-start", mt: 2, mb: 4 }}>
                {selectedOptions.map((option) => (
                    <Button key={option} sx={selectedOptions.includes(option) ? styles(colors).selectedColumnButton :
                        styles(colors).columnButton} onClick={() => removeSelectedOption(option)}>{option}</Button>
                ))}
                <Button onClick={() => setOpenOptionList(!openOptionList)}><MoreHorizIcon fontSize="medium" /></Button>
            </Box>
            <Box sx={{ display: "flex", flexWrap: "wrap", justifyContent: "flex-start", mt: 2, mb: 4 }}>
                <Button sx={{ marginLeft: 2, color: "#fff", bgcolor: "#635781", "&:hover": { bgcolor: "#63578180" } }} onClick={applyFilters}>Apply Filters</Button>
            </Box>

            <Dialog open={openOptionList} onClose={() => setOpenOptionList(false)}>
                <DialogTitle>Select Columns</DialogTitle>
                <DialogContent>
                    {columns.map((column) => (
                        <Button key={column} sx={selectedOptions.includes(column) ? styles(colors).selectedColumnButton :
                            styles(colors).columnButton} onClick={() => selectedOptions.includes(column) ? removeSelectedOption(column) : setSelectedOptions([...selectedOptions, column])}>{column}</Button>
                    ))}
                </DialogContent>
            </Dialog>
            <ReactApexChart
                options={chartData.options}
                series={chartData.series}
                type="line"
                height={450}
            />

            {chpData &&  chpData['CHP']['CHP-01'].length > 0 && (
                <Box sx={styles(colors).graphBox}>
                    <FilledOperationalModeFinalChart chpData={chpData} />
                </Box>
            )}
            {afrrProposalData && (
                <Box sx={styles(colors).graphBox}>
                    <FilledOperationalModePreChart afrrProposalData={afrrProposalData} />
                </Box>
            )}
            {afrrProposalData && (
                <Box sx={styles(colors).graphBox}>
                    <FilledOperationalModePreCharColumn afrrProposalData={afrrProposalData} />
                </Box>
            )}
        </>
    );

    const renderJsonTab = () => {
        // Only render JSON editors when the tab is active
        if (currentTab !== 1) {
            return (
                <Box sx={{ p: 2 }}>
                    <Typography variant="h6" sx={{ mb: 2, color: colors.grey[100] }}>
                        Enhanced JSON Editor with Fold & Compare
                    </Typography>
                    <Typography variant="body2" sx={{ color: colors.grey[300] }}>
                        Switch to JSON tab to view data editors
                    </Typography>
                </Box>
            );
        }

        return (
            <Box sx={{ p: 2 }}>
                <Typography variant="h6" sx={{ mb: 2, color: colors.grey[100] }}>
                    Enhanced JSON Editor with Fold & Compare
                </Typography>
                
                {/* Raw Data Section */}
                <Suspense fallback={
                    <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                        <CircularProgress />
                    </Box>
                }>
                    <EnhancedJsonEditor
                        data={rawData}
                        title="Raw Data"
                        colors={colors}
                        onSave={(newData) => setRawData(newData)}
                        readOnly={false}
                        defaultExpanded={true}
                        maxHeight="400px"
                    />
                </Suspense>

                {/* CHP Data Section */}
                <Suspense fallback={
                    <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                        <CircularProgress />
                    </Box>
                }>
                    <EnhancedJsonEditor
                        data={chpData}
                        title="CHP Data"
                        colors={colors}
                        onSave={(newData) => setChpData(newData)}
                        readOnly={false}
                        defaultExpanded={false}
                        maxHeight="400px"
                    />
                </Suspense>

                {/* AFRR Proposal Data Section */}
                <Suspense fallback={
                    <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                        <CircularProgress />
                    </Box>
                }>
                    <EnhancedJsonEditor
                        data={afrrProposalData}
                        title="AFRR Proposal Data"
                        colors={colors}
                        onSave={(newData) => setAfrrProposalData(newData)}
                        readOnly={false}
                        defaultExpanded={false}
                        maxHeight="400px"
                    />
                </Suspense>
            </Box>
        );
    };

    return (
        <Box sx={styles(colors).container}>
          {/*   {(loadingGetColumns || loadingGetData || loadingPredictionData) && (
                <LoadingScreen loading={loadingGetColumns || loadingGetData || loadingPredictionData} />
            )} */}
            <Box sx={styles(colors).graphBox}>
                <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
                    <Tabs 
                        value={currentTab} 
                        onChange={handleTabChange} 
                        sx={{
                            '& .MuiTab-root': {
                                color: colors.grey[100],
                                '&.Mui-selected': {
                                    color: colors.entras_green[100],
                                }
                            },
                            '& .MuiTabs-indicator': {
                                backgroundColor: colors.entras_green[100],
                            }
                        }}
                    >
                        <Tab label="Graphs" />
                        <Tab label="JSON" />
                    </Tabs>
                </Box>
                
                {currentTab === 0 && renderGraphsTab()}
                {currentTab === 1 && renderJsonTab()}
            </Box>
        </Box >
    );
};

export default Custom

const styles = (colors) => ({
    container: {
        justifyContent: "center",
        width: "95%",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        marginLeft: "2.5%",
        paddingLeft: "50px",
    },
    columnButton: {
        width: "160px",
        backgroundColor: colors.greenAccent[100],
        borderRadius: "10px",
        margin: "10px",
        flexWrap: "wrap",
        color: "white",
        padding: "20px",
        fontSize: "10px",
        "&:hover": {
            backgroundColor: colors.greenAccent[200],
        }
    },
    selectedColumnButton: {
        //width: "260px",
        maxWidth: "260px",
        backgroundColor: colors.entras_green[100],
        borderRadius: "10px",
        color: "white",
        margin: "10px",
        flexWrap: "wrap",
        padding: "15px",
        fontSize: "11px",
        "&:hover": {
            backgroundColor: colors.entras_green[200],
        }
    },
    graphBox: {
        width: "100%",
        height: "100%",
        backgroundColor: "#fff",
        borderRadius: 3,
        alignItems: "center",
        justifyContent: "center",
        width: "100%",
        boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.1)",
        p: 2,
        pb: 4,
        alignContent: "center",
        marginTop: "20px",
    }
})

